import React, { useEffect, useMemo, useState } from "react";
import { Modal, Radio, Typography, Empty, Space, Tag as AntTag } from "antd";
import { useTheme } from "../../../contexts/ThemeContext";
import { Alert } from "../../../components/ui";
import { MERGE_MODE } from "../utils/mergeSuggestion";

const CONFIDENCE_COLOR = { alta: "green", media: "gold", baixa: "red" };
const CONFIDENCE_LABEL = { alta: "Alta", media: "Média", baixa: "Baixa" };

/**
 * Preview da sugestão da IA. Nada é salvo aqui: "Aplicar na tela" só preenche o estado do submenu,
 * o usuário revisa e persiste pelo "Salvar Alterações" de sempre.
 */
const AiSuggestionModal = ({ open, suggestion, typeLabels, hasExisting, onApply, onCancel }) => {
  const { theme } = useTheme();
  const [mode, setMode] = useState(MERGE_MODE.complete);

  useEffect(() => {
    if (open) setMode(hasExisting ? MERGE_MODE.complete : MERGE_MODE.replace);
  }, [open, hasExisting]);

  // Agrupa por tipo, na ordem de typeLabels: [{ type, label, items: [{ key, title, confidence, justification }] }]
  const groups = useMemo(() => {
    if (!suggestion) return [];
    const isDre = suggestion.target === "dre";
    const rows = isDre
      ? (suggestion.indicators || []).map((i) => ({
          key: `dre-${i.type}`,
          type: i.type,
          title: (i.dreLabels || []).join(" + "),
          justification: i.justification,
        }))
      : (suggestion.assignments || []).map((a) => ({
          key: `cat-${a.codCategoria}`,
          type: a.type,
          title: a.descricao || a.codCategoria,
          confidence: a.confidence,
          justification: a.justification,
        }));

    return Object.entries(typeLabels)
      .map(([type, label]) => ({
        type: Number(type),
        label,
        items: rows.filter((r) => Number(r.type) === Number(type)),
      }))
      .filter((g) => g.items.length > 0);
  }, [suggestion, typeLabels]);

  const total = groups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <Modal
      open={open}
      title="Sugestão de configuração por IA"
      okText="Aplicar na tela"
      cancelText="Descartar"
      okButtonProps={{ disabled: total === 0 }}
      onOk={() => onApply(mode)}
      onCancel={onCancel}
      width={720}
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          A sugestão só é gravada quando você clicar em "Salvar Alterações". Revise antes de salvar.
          {suggestion?.usedCompanyContext && " Gerado considerando o contexto cadastrado desta empresa."}
        </Typography.Text>

        {hasExisting && total > 0 && (
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Space direction="vertical">
              <Radio value={MERGE_MODE.complete}>
                Completar apenas o que falta <Typography.Text type="secondary">(mantém os vínculos atuais)</Typography.Text>
              </Radio>
              <Radio value={MERGE_MODE.replace}>
                Substituir tudo <Typography.Text type="secondary">(a configuração passa a ser exatamente a sugestão)</Typography.Text>
              </Radio>
            </Space>
          </Radio.Group>
        )}

        {(suggestion?.warnings || []).length > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Pontos de atenção"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {suggestion.warnings.map((w, idx) => (
                  <li key={idx}>{w}</li>
                ))}
              </ul>
            }
          />
        )}

        {total === 0 ? (
          <Empty description="A IA não encontrou vínculos com segurança suficiente." />
        ) : (
          <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
            {groups.map((group) => (
              <div
                key={group.type}
                style={{
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                  padding: "8px 12px",
                  marginBottom: 8,
                  backgroundColor: theme.cardBackground,
                }}
              >
                <Typography.Text strong style={{ color: theme.textPrimary }}>
                  {group.label} <Typography.Text type="secondary">({group.items.length})</Typography.Text>
                </Typography.Text>
                {group.items.map((item) => (
                  <div key={item.key} style={{ padding: "6px 0", borderTop: `1px dashed ${theme.border}` }}>
                    <Space size={6} wrap>
                      <Typography.Text style={{ color: theme.textPrimary }}>{item.title}</Typography.Text>
                      {item.confidence && (
                        <AntTag color={CONFIDENCE_COLOR[item.confidence]} style={{ marginInlineEnd: 0 }}>
                          {CONFIDENCE_LABEL[item.confidence] || item.confidence}
                        </AntTag>
                      )}
                    </Space>
                    {item.justification && (
                      <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        {item.justification}
                      </Typography.Text>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default AiSuggestionModal;
